import _ from 'lodash';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/de';
import 'moment/locale/th';
import 'moment/locale/zh-cn';

import { showLoading, hideLoading } from '../reducers/preloader';
import { LOCALE_FETCH_FAILED } from '../types';
import settings from '../../config/settings';
import { sendCommand, isDevice } from '../../utils/mobile';

const LOCALE_URL = `${settings.locale_url}`;
export const LANG_PREFERENCE = 'keypr_language';

/**
 * List of Languages
 * - isEnabled flag to show/hide the language option in the dropdown
 */
export const languages = [
  {
    code: 'en',
    title: 'English',
    isEnabled: true,
  },
  {
    code: 'es_ES',
    title: 'Español',
    isEnabled: true,
  },
  {
    code: 'fr_FR',
    title: 'Français',
    isEnabled: true,
  },
  {
    code: 'it_IT',
    title: 'Italiano',
    isEnabled: true,
  },
  {
    code: 'de_DE',
    title: 'Deutsch',
    isEnabled: true,
  },
  {
    code: 'th_TH',
    title: 'ไทย',
    isEnabled: true,
  },
  {
    code: 'zh_CN',
    title: '简体中文',
    isEnabled: true,
  },
];

/**
 * Function to post language message to native component
 * @param {String} lang - language code eg. en, fr
 */
export const setDeviceLanguage = (lang) => {
  sendCommand('language-change', { language: lang });
};

/**
 * Function to check whether the language is available in the moment locale list
 * @param {String} langCode - language code eg. en, fr
 */
const isMomentLocaleAvailable = (langCode) => {
  const momentLocales = moment.locales();
  return _.includes(momentLocales, langCode);
};

/**
 * Function to handle Moment Libraries locale changes
 * @param {String} lang - language code eg. en, fr
 */
const handleMomentLocaleChange = (lang) => {
  const langCodeWithCountry = lang.toLowerCase().replace('_', '-');
  if (isMomentLocaleAvailable(langCodeWithCountry)) {
    moment.locale(langCodeWithCountry);
  } else {
    const langCode = langCodeWithCountry.split('-')[0];
    if (isMomentLocaleAvailable(langCode)) {
      moment.locale(langCode);
    }
  }
};

/**
 * Function to handle External Libraries locale changes
 * @param {String} lang - language code eg. en, fr
 */
export const handleLanguageChange = (lang) => {
  // moment library locale changes
  handleMomentLocaleChange(lang);
};

/**
 * Function to fetch language json from Amazon S3.
 * @param {String} lang - language code eg. en, fr
 * @param {Boolean} showLoader - true to show loader during fetching. defaults to false
 */
export const fetchTranslations = (lang, showLoader = false, setTranslations, setLanguage) => (dispatch) => {
  if (showLoader) {
    dispatch(showLoading(true));
  }
  return fetch(`${LOCALE_URL}${lang}.json`)
    .then(res => res.json())
    .then((json) => {
      setTranslations(json, { preserveExisting: true });
      handleLanguageChange(lang);
      setLanguage(lang);
      if (isDevice()) {
        setDeviceLanguage(lang);
        window.localStorage.setItem(LANG_PREFERENCE, lang);
      }
      if (showLoader) {
        dispatch(hideLoading());
      }
      return json;
    })
    .catch((error) => {
      if (showLoader) {
        dispatch(hideLoading());
      }
      dispatch({
        type: LOCALE_FETCH_FAILED,
        payload: error,
      });
      return error;
    });
};

/**
 * Function to detect browser Language Preferences
 */
const getBrowserLanguagePreference = () => {
  const browserLanguagePropertyKeys = ['languages', 'language', 'browserLanguage', 'userLanguage', 'systemLanguage'];
  const availableLanguages = languages.filter(lang => lang.isEnabled).map(lang => lang.code);
  const detectedLocale = _.chain(window.navigator)
    .pick(browserLanguagePropertyKeys)
    .values()
    .flatten()
    .compact()
    .uniqBy()
    .map(x => x.replace('-', '_'))
    // .find(x => _.includes(availableLanguages, x))
    .head()
    .value();
  const locale = _.includes(availableLanguages, detectedLocale) ? detectedLocale : 'en';
  return locale;
};

/**
 * Function to detect device Language Preferences
 */
const getDeviceLanguagePreference = () => {
  let lang = window.localStorage.getItem(LANG_PREFERENCE);
  if (!lang) {
    lang = getBrowserLanguagePreference();
  }
  return lang;
};

/**
 * Invoked to get language code from preferences
 *  - Mobile - checks for a language in local storage, if not get from browser preferences
 *  - Web - get the language from browser preferences
 */
export const getLanguage = () => {
  let lang = 'en';
  if (isDevice()) {
    lang = getDeviceLanguagePreference();
  } else {
    lang = getBrowserLanguagePreference();
  }
  return lang;
};



// WEBPACK FOOTER //
// ./src/state/actions/locale.js